import "@/index.css";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Calendar, Clock, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString('de-AT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function DemoConfirm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [selectedAlternative, setSelectedAlternative] = useState(null);

  useEffect(() => {
    fetch(`${API}/demo/confirm/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Request not found');
        return res.json();
      })
      .then(data => {
        setRequest(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Anfrage nicht gefunden oder Link abgelaufen.');
        setLoading(false);
      });
  }, [token]);

  const handleConfirm = async () => {
    if (!selectedAlternative) {
      toast.error('Bitte wähle einen Termin aus');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/demo/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'accept',
          selected_alternative: selectedAlternative
        })
      });

      if (response.ok) {
        toast.success('Termin bestätigt! Du erhältst eine Bestätigungs-E-Mail.');
        // Refresh data
        const updated = await fetch(`${API}/demo/confirm/${token}`).then(r => r.json());
        setRequest(updated);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Fehler beim Bestätigen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/demo/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reject' })
      });

      if (response.ok) {
        toast.success('Alternativen abgelehnt. Du kannst einen neuen Termin buchen.');
        const updated = await fetch(`${API}/demo/confirm/${token}`).then(r => r.json());
        setRequest(updated);
      } else {
        toast.error('Fehler beim Ablehnen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Fehler</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/schedule-demo" 
            className="inline-block px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          >
            Neuen Termin buchen
          </Link>
        </div>
      </div>
    );
  }

  // Status: Accepted with meeting link
  if (request.status === 'accepted' && request.meeting_link) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Termin bestätigt!</h1>
          <p className="text-gray-600 mb-6">
            Deine Demo für <strong>{request.app_name}</strong> wurde bestätigt.
          </p>
          
          <div className="bg-purple-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-900">
                {formatDateTime(request.selected_alternative || request.requested_slot)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="text-purple-700">30 Minuten</span>
            </div>
          </div>

          {request.admin_notes && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-gray-600"><strong>Hinweis:</strong> {request.admin_notes}</p>
            </div>
          )}

          <a
            href={request.meeting_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            Zum Meeting beitreten
          </a>
          
          <p className="text-sm text-gray-500 mt-4">
            Meeting-Link: <a href={request.meeting_link} className="text-purple-600 hover:underline">{request.meeting_link}</a>
          </p>
        </motion.div>
      </div>
    );
  }

  // Status: Alternatives proposed - customer can choose
  if (request.status === 'alternatives_proposed' && request.proposed_alternatives?.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full"
        >
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Alternative Termine</h1>
            <p className="text-gray-600">
              Dein gewünschter Termin für <strong>{request.app_name}</strong> ist leider nicht verfügbar. 
              Bitte wähle einen der folgenden Alternativen:
            </p>
          </div>

          {request.admin_notes && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-600"><strong>Nachricht:</strong> {request.admin_notes}</p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            {request.proposed_alternatives.map((alt, index) => (
              <button
                key={index}
                onClick={() => setSelectedAlternative(alt)}
                className={`
                  w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between
                  ${selectedAlternative === alt 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-200 hover:border-purple-300'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-gray-900">{formatDateTime(alt)}</span>
                </div>
                {selectedAlternative === alt && (
                  <Check className="w-5 h-5 text-purple-600" />
                )}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleReject}
              disabled={submitting}
              className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Ablehnen
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedAlternative || submitting}
              className={`
                flex-1 py-3 px-4 rounded-xl font-semibold text-white transition-all
                ${selectedAlternative && !submitting
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                  : 'bg-gray-300 cursor-not-allowed'
                }
              `}
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Bestätigen'}
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/schedule-demo" className="text-purple-600 hover:underline">
              Oder buche einen komplett neuen Termin
            </Link>
          </p>
        </motion.div>
      </div>
    );
  }

  // Status: Alternative confirmed - waiting for admin
  if (request.status === 'alternative_confirmed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Termin ausgewählt!</h1>
          <p className="text-gray-600 mb-6">
            Du hast einen alternativen Termin für <strong>{request.app_name}</strong> ausgewählt. 
            Wir bestätigen dir den Termin in Kürze per E-Mail.
          </p>
          
          <div className="bg-purple-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-900">
                {formatDateTime(request.selected_alternative)}
              </span>
            </div>
          </div>

          <Link 
            to="/" 
            className="inline-block px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
          >
            Zurück zur Startseite
          </Link>
        </motion.div>
      </div>
    );
  }

  // Status: Rejected
  if (request.status === 'rejected' || request.status === 'alternative_rejected') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-gray-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Anfrage geschlossen</h1>
          <p className="text-gray-600 mb-6">
            Diese Demo-Anfrage wurde abgelehnt oder ist nicht mehr aktiv.
          </p>
          
          <Link 
            to="/schedule-demo" 
            className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all"
          >
            Neuen Termin buchen
          </Link>
        </motion.div>
      </div>
    );
  }

  // Status: Pending
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
      >
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-yellow-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Anfrage in Bearbeitung</h1>
        <p className="text-gray-600 mb-6">
          Deine Demo-Anfrage für <strong>{request.app_name}</strong> wird bearbeitet. 
          Du erhältst eine E-Mail, sobald wir deinen Termin bestätigt haben.
        </p>
        
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">
              Angefragter Termin: {formatDateTime(request.requested_slot)}
            </span>
          </div>
        </div>

        <Link 
          to="/" 
          className="inline-block px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
        >
          Zurück zur Startseite
        </Link>
      </motion.div>
    </div>
  );
}
